import * as React from 'react';
import { ErrorPage } from '@web-config-app/core-react-ui';
import { useConfigApp } from '@web-config-app/core-react';
import { buildTenantPath } from '@web-config-app/core';
import { useCanAccessModule } from '../../hooks/use-can-access-module/use-can-access-module';
import { LoadingIndicator } from '../loading-indicator.component';

interface PermissionsGuardProps {
  permissions?: string[];
  children: React.ReactNode;
}

/**
 * Route guard that checks module-level permissions before rendering children.
 *
 * - No permissions or empty array: visible to all authenticated users
 * - Contains 'admin': checks isAdminUser()
 * - Non-admin strings: checked against the user's JWT claims using
 *   `hasAllPermissions`, matching how entity-level permissions work in
 *   `filterEntitiesByPermissions`.
 */
export const PermissionsGuard = ({
  permissions,
  children,
}: PermissionsGuardProps): React.ReactElement | null => {
  const { tenantId } = useConfigApp();
  const { canAccess, isLoaded } = useCanAccessModule(permissions);

  if (!isLoaded) {
    return <LoadingIndicator />;
  }

  if (!canAccess) {
    return <ErrorPage homeUrl={buildTenantPath(tenantId)} />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
